/**
 * Cookie consent — CookieConsent v3 (https://cookieconsent.orestbida.com/).
 *
 * Loaded synchronously at the top of <head> (base/dochead.twig) so it runs before any
 * tracker: Consent Mode has to be denied before GTM boots. The site is statically cached,
 * so all gating is necessarily client-side.
 *
 * Trackers are held back by writing their <script> as type="text/plain" data-category.
 */

import * as CookieConsent from 'vanilla-cookieconsent';

// Read by the footer "Cookie Settings" link and by components/hubspot-tracking.js.
window.CookieConsent = CookieConsent;

/* Google Consent Mode v2 — denied by default, before GTM loads. */
window.dataLayer = window.dataLayer || [];
function gtag() { window.dataLayer.push(arguments); }
window.gtag = window.gtag || gtag;

gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
});

function syncGoogleConsent() {
    const analytics = CookieConsent.acceptedCategory('analytics');
    const marketing = CookieConsent.acceptedCategory('marketing');

    gtag('consent', 'update', {
        analytics_storage: analytics ? 'granted' : 'denied',
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: marketing ? 'granted' : 'denied',
        ad_personalization: marketing ? 'granted' : 'denied',
    });

    // Consent Mode only governs Google's own tags; LinkedIn and HubSpot fire from inside
    // the container and ignore it. Mirror the state into the dataLayer so those tags can be
    // gated by a trigger condition in GTM.
    window.dataLayer.push({
        event: 'cookie_consent_update',
        cookie_consent_analytics: analytics,
        cookie_consent_marketing: marketing,
    });
}

/**
 * Wistia's doNotTrack flag suppresses its stats cookies but still plays the video.
 *
 * Both directions: templates bake the flag in as the fail-safe, but videoGroup.twig prints
 * CMS-authored embed HTML we can't pre-flag, so add it where missing and strip it on
 * consent. Runs before lazysizes copies data-src -> src.
 */
function syncWistiaTracking() {
    const allowed = CookieConsent.acceptedCategory('analytics');

    document.querySelectorAll('iframe[data-src*="wistia"], iframe[src*="wistia"]').forEach((iframe) => {
        ['data-src', 'src'].forEach((attr) => {
            const value = iframe.getAttribute(attr);
            if (!value) {
                return;
            }

            const flagged = value.includes('doNotTrack=true');

            if (allowed && flagged) {
                iframe.setAttribute(
                    attr,
                    value.replace(/([?&])doNotTrack=true&?/, '$1').replace(/[?&]$/, ''),
                );
            } else if (!allowed && !flagged) {
                iframe.setAttribute(attr, value + (value.includes('?') ? '&' : '?') + 'doNotTrack=true');
            }
        });
    });
}

/**
 * Every domain a cookie here could have been written with: host-only, then each parent up
 * to the TLD. document.cookie never exposes a cookie's domain and a delete only lands on
 * an exact match, so try them all — GA picks the broadest domain that accepts the write,
 * which on cast-iron-media.ddev.site is `.ddev.site`, not the site's own host.
 */
function cookieDomains() {
    const parts = window.location.hostname.split('.');
    const domains = [null]; // host-only, i.e. no domain attribute

    for (let i = 0; i < parts.length - 1; i += 1) {
        domains.push('.' + parts.slice(i).join('.'));
    }

    return domains;
}

const COOKIE_DOMAINS = cookieDomains();

function deleteCookie(name) {
    COOKIE_DOMAINS.forEach((domain) => {
        document.cookie = name + '=; Max-Age=0; path=/' + (domain ? '; domain=' + domain : '');
    });
}

/**
 * Delete the cookies of any rejected category.
 *
 * autoClear alone isn't enough: it fires the moment a category is switched off, while the
 * tag that owns the cookie is still live, and GA4/HubSpot write theirs straight back. This
 * runs on every page load instead, once the gate is already up and nothing can race it.
 */
function sweepRejectedCookies() {
    const present = document.cookie.split('; ').map((pair) => pair.split('=')[0]).filter(Boolean);

    Object.keys(TRACKER_COOKIES).forEach((category) => {
        if (CookieConsent.acceptedCategory(category)) {
            return;
        }

        TRACKER_COOKIES[category].forEach(({ name }) => {
            present
                .filter((cookie) => (name instanceof RegExp ? name.test(cookie) : cookie === name))
                .forEach(deleteCookie);
        });
    });
}

function onConsentResolved() {
    syncGoogleConsent();
    syncWistiaTracking();
    sweepRejectedCookies();
}

// false hides the banner and grants every category (dochead.twig, from config/custom.php).
const consentEnabled = window.COOKIE_CONSENT_ENABLED !== false;

// www.castiron.media -> .castiron.media, which is where GA and friends write.
const COOKIE_DOMAIN = '.' + window.location.hostname.replace(/^www\./, '');

/**
 * Cookies per category, shared by autoClear and sweepRejectedCookies so they can't drift.
 * First-party only — cookies on a third party's own domain (LinkedIn, Wistia) can't be
 * deleted from here, and are handled by not letting the tag run. `hostOnly` marks the ones
 * we write ourselves with no domain attribute.
 */
const TRACKER_COOKIES = {
    analytics: [
        // One _ga_<propertyId> per GA4 property. Two fire: the container's, plus one Google
        // links to the Ads tag. Both analytics_storage-governed, so match the prefix.
        { name: /^_ga/ },
        { name: '_gid' },
        { name: /^_gat/ },
    ],
    marketing: [
        // Google Ads conversion linker — ad_storage-governed, so it belongs here.
        { name: /^_gcl_/ },
        // Leadfeeder writes _lfa — not the _ldfdr name used by its on-page global.
        { name: /^_lfa/ },
        // HubSpot, fired from inside the GTM container.
        { name: 'hubspotutk' },
        { name: /^__hs/ },
        // Ours — first-touch location attribution.
        { name: 'location_city', hostOnly: true },
        { name: 'location_url', hostOnly: true },
        { name: 'location_first_visit_date', hostOnly: true },
    ],
};

const autoClearFor = (category) => ({
    cookies: TRACKER_COOKIES[category].map(({ name, hostOnly }) =>
        hostOnly ? { name } : { name, domain: COOKIE_DOMAIN }),
});

const ccConfig = {
    guiOptions: {
        consentModal: { layout: 'box wide', position: 'bottom left' },
        preferencesModal: { layout: 'box' },
    },

    categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: { autoClear: autoClearFor('analytics') },
        marketing: { autoClear: autoClearFor('marketing') },
    },

    autoShow: consentEnabled,

    onFirstConsent: onConsentResolved,
    onConsent: onConsentResolved,
    // Reload on a later change so trackers re-initialise cleanly. With the banner disabled
    // we re-sync instead, so the forced accept below can't loop.
    onChange: consentEnabled ? () => window.location.reload() : onConsentResolved,

    language: {
        default: 'en',
        translations: {
            en: {
                consentModal: {
                    title: 'We use cookies',
                    description:
                        'We use cookies to run the site, measure how it’s used, and understand which ' +
                        'companies are interested in our work. You can accept all, reject everything ' +
                        'non-essential, or choose for yourself.',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    showPreferencesBtn: 'Manage preferences',
                },
                preferencesModal: {
                    title: 'Cookie preferences',
                    acceptAllBtn: 'Accept all',
                    acceptNecessaryBtn: 'Reject all',
                    savePreferencesBtn: 'Save preferences',
                    closeIconLabel: 'Close',
                    sections: [
                        {
                            title: 'Strictly necessary',
                            description: 'Required for the site to work. Always on.',
                            linkedCategory: 'necessary',
                            cookieTable: {
                                headers: { name: 'Cookie', service: 'Service', desc: 'Purpose', exp: 'Expiry' },
                                body: [
                                    { name: 'CraftSessionId', service: 'Cast Iron Media', desc: 'Keeps your session alive as you browse', exp: 'Session' },
                                    { name: 'CRAFT_CSRF_TOKEN', service: 'Cast Iron Media', desc: 'Protects forms against cross-site request forgery', exp: 'Session' },
                                    { name: 'cc_cookie', service: 'Cast Iron Media', desc: 'Stores the cookie choices you make here', exp: '6 months' },
                                    { name: 'freeform_*', service: 'Cast Iron Media', desc: 'Spam protection and submission handling on forms', exp: 'Session' },
                                    { name: '__cf_bm', service: 'Cloudflare', desc: 'Bot protection', exp: '30 minutes' },
                                ],
                            },
                        },
                        {
                            title: 'Analytics',
                            description:
                                'Google Analytics (loaded through Google Tag Manager) and Wistia video ' +
                                'stats — how many people visit, which pages they read, and which videos ' +
                                'they watch. Videos still play if you decline; we just don’t count the view.',
                            linkedCategory: 'analytics',
                            cookieTable: {
                                headers: { name: 'Cookie', service: 'Service', desc: 'Purpose', exp: 'Expiry' },
                                body: [
                                    { name: '_ga', service: 'Google Analytics', desc: 'Distinguishes unique visitors', exp: '2 years' },
                                    { name: '_ga_*', service: 'Google Analytics', desc: 'GA4 session state', exp: '2 years' },
                                    { name: '_gid', service: 'Google Analytics', desc: 'Distinguishes unique visitors', exp: '24 hours' },
                                    { name: 'wistia*', service: 'Wistia', desc: 'Counts video plays and playback position', exp: '1 year' },
                                ],
                            },
                        },
                        {
                            title: 'Marketing',
                            description:
                                'Leadfeeder, LinkedIn and HubSpot — these tell us which companies visit, ' +
                                'which campaign brought you here, and let us follow up on enquiries. ' +
                                'Declining does not stop you using our forms.',
                            linkedCategory: 'marketing',
                            cookieTable: {
                                headers: { name: 'Cookie', service: 'Service', desc: 'Purpose', exp: 'Expiry' },
                                body: [
                                    { name: '_lfa', service: 'Leadfeeder', desc: 'Identifies the company a visitor is browsing from', exp: '1–2 years' },
                                    { name: 'hubspotutk', service: 'HubSpot', desc: 'Links form submissions to a visitor’s browsing history', exp: '6 months' },
                                    { name: 'location_city', service: 'Cast Iron Media', desc: 'Remembers the first market page you landed on', exp: '1 year' },
                                    { name: 'location_url', service: 'Cast Iron Media', desc: 'Remembers the first market page URL you landed on', exp: '1 year' },
                                    { name: 'location_first_visit_date', service: 'Cast Iron Media', desc: 'Records when you first visited a market page', exp: '1 year' },
                                    { name: 'li_sugr / UserMatchHistory', service: 'LinkedIn', desc: 'Ad targeting and conversion measurement', exp: 'Up to 6 months' },
                                ],
                            },
                        },
                        {
                            title: 'More information',
                            description:
                                'See our <a href="' + (window.COOKIE_POLICY_URL || '/terms/') + '">Terms &amp; Conditions</a> ' +
                                'for details, or <a href="/contact/">get in touch</a> with any questions.',
                        },
                    ],
                },
            },
        },
    },
};

// run() appends the banner to document.body, which doesn't exist yet in <head>.
function startConsent() {
    CookieConsent.run(ccConfig);

    // Banner disabled, so grant everything ourselves, as an "Accept all" click would.
    if (!consentEnabled) {
        CookieConsent.acceptCategory('all');
    }

    // onConsent only fires once a choice exists, so a first-time visitor never reaches it —
    // but Wistia has to be flagged before that decision, not after.
    syncWistiaTracking();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startConsent);
} else {
    startConsent();
}
